import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * Split text animation using GSAP SplitText.
 * Animates text by chars, words, or lines on scroll.
 *
 * @param {Object} options
 * @param {string} options.type - 'chars'|'words'|'lines' (default 'chars')
 * @param {string} options.animation - 'fade-up'|'wave'|'reveal' (default 'fade-up')
 * @param {number} options.duration - seconds per element (default 0.5)
 * @param {number} options.stagger - delay between elements (default 0.03)
 * @param {string} options.start - ScrollTrigger start (default 'top 80%')
 * @param {boolean} options.disabled
 * @returns {React.RefObject}
 */
export default function useSplitText(options = {}) {
    const ref = useRef(null);
    const {
        type = 'chars',
        animation = 'fade-up',
        duration = 0.5,
        stagger = 0.03,
        start = 'top 80%',
        disabled = false,
    } = options;

    useEffect(() => {
        if (disabled || !ref.current) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const el = ref.current;
        let ctx = gsap.context(() => {
            const split = SplitText.create(el, { type });
            const targets = split[type]; // split.chars, split.words, or split.lines

            const fromVars = { opacity: 0 };

            switch (animation) {
                case 'fade-up':
                    fromVars.y = 20;
                    break;
                case 'wave':
                    fromVars.y = 30;
                    fromVars.rotationX = -40;
                    break;
                case 'reveal':
                    fromVars.y = '100%';
                    // Wrap each element for clip masking
                    targets.forEach(t => {
                        t.style.display = 'inline-block';
                        t.parentElement.style.overflow = 'hidden';
                    });
                    break;
                default:
                    fromVars.y = 20;
            }

            gsap.fromTo(targets, fromVars, {
                opacity: 1, y: 0, rotationX: 0,
                duration, stagger, ease: 'power2.out',
                scrollTrigger: { trigger: el, start, once: true },
            });
        }, el);

        return () => ctx.revert();
    }, [type, animation, duration, stagger, start, disabled]);

    return ref;
}
